// Library methods
import { ReactNode } from "react";

// Components
import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

export const ACCENT_TEAL = "#1B8F76";

type ChoiceCardType = {
  name: string;
  value: string;
  checked: boolean;
  title: string;
  description?: string;
  badge?: string;
  onSelect: () => void;
  children?: ReactNode;
};

const ChoiceCard = ({
  name,
  value,
  checked,
  title,
  description,
  badge,
  onSelect,
  children,
}: ChoiceCardType) => {
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      component="label"
      sx={{
        display: "flex",
        gap: "8px",
        alignItems: "flex-start",
        cursor: "pointer",
        borderRadius: "8px",
        padding: checked ? "11px" : "12px",
        border: checked
          ? `2px solid ${ACCENT_TEAL}`
          : `1px solid ${isDark ? "#8A8DA2" : "rgba(0, 0, 0, 0.2)"}`,
        backgroundColor: checked
          ? isDark
            ? "#2A2C3F"
            : "rgba(27, 143, 118, 0.08)"
          : "transparent",
      }}
    >
      <Radio
        name={name}
        value={value}
        checked={checked}
        onChange={onSelect}
        sx={{
          padding: "0px",
          color: onSurface,
          "&.Mui-checked": { color: ACCENT_TEAL },
        }}
      />
      <Box display="flex" flexDirection="column" gap="4px" minWidth={0}>
        <Box display="flex" gap="8px" alignItems="center" flexWrap="wrap">
          <Typography fontSize="14px" fontWeight={700} color={onSurface}>
            {title}
          </Typography>
          {badge && (
            <Typography
              fontSize="11px"
              fontWeight={700}
              sx={{
                color: "#0B3B30",
                backgroundColor: "#7FD9C2",
                borderRadius: "999px",
                padding: "1px 8px",
              }}
            >
              {badge}
            </Typography>
          )}
        </Box>
        {description && (
          <Typography fontSize="12px" color={mutedText} lineHeight={1.4}>
            {description}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
};

export default ChoiceCard;
